import {
  isOrderDetail,
  isTableSnapshot,
  type OrderDetail,
  type TableSnapshot,
} from "@billiard/contracts";

export interface SessionTransferCommand {
  kind: "TRANSFER_SESSION";
  sessionId: string;
  sourceTable: TableSnapshot;
  targetTable: TableSnapshot;
  baselineOrder: OrderDetail;
  reason: string;
  idempotencyKey: string;
}

export type SessionTransferRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface SessionTransferRecovery {
  ownerIdentityKey: string;
  command: SessionTransferCommand;
  state: SessionTransferRecoveryState;
}

export type SessionTransferRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: SessionTransferRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const SESSION_TRANSFER_RECOVERY_STORAGE_KEY = "billiard-erp:session-transfer-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "MEMBER_CHECKOUT_IN_PROGRESS",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "ORIGIN_REJECTED",
  "ORDER_CONFLICT",
  "ORDER_CONFLICT_ISOLATED",
  "PACKAGE_TRANSFER_REPRICE_UNAVAILABLE",
  "SESSION_NOT_OPEN",
  "SESSION_OPERATION_REASON_REQUIRED",
  "SESSION_OPERATION_REASON_TOO_LONG",
  "TABLE_NOT_FOUND",
  "TARGET_TABLE_UNAVAILABLE",
]);

let pendingRecovery: SessionTransferRecovery | null = null;
let dispatchingRecovery: SessionTransferRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function sessionTransferIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isSessionTransferCommand(value: unknown): value is SessionTransferCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "kind",
      "sessionId",
      "sourceTable",
      "targetTable",
      "baselineOrder",
      "reason",
      "idempotencyKey",
    ]) ||
    value.kind !== "TRANSFER_SESSION" ||
    !isBoundedString(value.sessionId, 1, 256) ||
    value.sessionId !== value.sessionId.trim() ||
    !isTableSnapshot(value.sourceTable) ||
    !isTableSnapshot(value.targetTable) ||
    value.sourceTable.tenantId !== value.targetTable.tenantId ||
    value.sourceTable.storeId !== value.targetTable.storeId ||
    value.sourceTable.tableId === value.targetTable.tableId ||
    value.sourceTable.state !== "IN_USE" ||
    value.sourceTable.sessionId !== value.sessionId ||
    value.sourceTable.sessionStartedAt === null ||
    value.targetTable.state !== "IDLE" ||
    value.targetTable.sessionId !== null ||
    value.targetTable.sessionStartedAt !== null ||
    value.targetTable.billingState !== "ENDED" ||
    value.targetTable.billingPausedAt !== null ||
    value.targetTable.pricingPlanName !== null ||
    value.targetTable.estimatedFeeFen !== 0 ||
    value.targetTable.currentTotalFen !== 0 ||
    value.targetTable.sessionElapsedSeconds !== 0 ||
    !isOrderDetail(value.baselineOrder) ||
    value.baselineOrder.status !== "OPEN" ||
    value.baselineOrder.sessionId !== value.sessionId ||
    value.baselineOrder.tableId !== value.sourceTable.tableId ||
    value.baselineOrder.tableNumber !== value.sourceTable.tableNumber ||
    value.baselineOrder.startedAt !== value.sourceTable.sessionStartedAt ||
    value.baselineOrder.billingState !== value.sourceTable.billingState ||
    value.baselineOrder.billingPausedAt !== value.sourceTable.billingPausedAt ||
    value.baselineOrder.priceSnapshot.name !== value.sourceTable.pricingPlanName ||
    value.baselineOrder.priceSnapshot.evidence !== value.sourceTable.billingEvidence ||
    value.baselineOrder.tableFeeFen !== value.sourceTable.estimatedFeeFen ||
    value.baselineOrder.totalFen !== value.sourceTable.currentTotalFen ||
    !isBoundedString(value.reason, 2, 200) ||
    value.reason !== value.reason.trim() ||
    !isBoundedString(value.idempotencyKey, 8, 128) ||
    value.idempotencyKey !== value.idempotencyKey.trim()
  ) {
    return false;
  }
  return true;
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: SessionTransferCommand,
  state: SessionTransferRecoveryState,
): SessionTransferRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): SessionTransferRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isSessionTransferCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function recoveryEquals(left: SessionTransferRecovery, right: SessionTransferRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): SessionTransferRecovery | null {
  const encoded = storage.getItem(SESSION_TRANSFER_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: SessionTransferRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      SESSION_TRANSFER_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: SessionTransferRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(SESSION_TRANSFER_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readSessionTransferRecovery(identityKey: string): SessionTransferRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimSessionTransferRecovery(
  identityKey: string,
  command: SessionTransferCommand,
): SessionTransferRecoveryView {
  const current = readSessionTransferRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isSessionTransferCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: SessionTransferRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginSessionTransferDispatch(
  recovery: SessionTransferRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishSessionTransferDispatch(
  recovery: SessionTransferRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markSessionTransferOutcomeUnknown(
  recovery: SessionTransferRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearSessionTransferRecovery(
  recovery: SessionTransferRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(SESSION_TRANSFER_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(SESSION_TRANSFER_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function sessionTransferOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

export function resetSessionTransferRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(SESSION_TRANSFER_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetSessionTransferRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
